import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import { useUpdatePortalUserMutation } from "../../../../services/ProfileService";
import { useGetPhysicianGroupsByCompanyQuery } from "../../../../services/ConfigurationService";
import type { PortalUserView } from "../../../../services/ProfileService/types/portalUserTypes";
import type { PhysicianGroupType } from "../../../../services/ConfigurationService/types/physicianGroupType";

interface EditPortalUserDialogProps {
  open: boolean;
  onClose: () => void;
  user: PortalUserView | null;
  company: string;
}

export function EditPortalUserDialog({ open, onClose, user, company }: EditPortalUserDialogProps) {
  const [selectedGroup, setSelectedGroup] = useState<PhysicianGroupType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [updateUser, { isLoading }] = useUpdatePortalUserMutation();
  const { data: groups = [], isLoading: isLoadingGroups } = useGetPhysicianGroupsByCompanyQuery(
    { company },
    { skip: !open || !company }
  );

  useEffect(() => {
    if (user && groups.length > 0) {
      const current = groups.find((g) => g.id === user.practiceId) || null;
      setSelectedGroup(current);
    }
  }, [user, groups]);

  const handleSubmit = async () => {
    if (!user || !selectedGroup) return;
    setError(null);

    if (selectedGroup.id === user.practiceId) {
      onClose();
      return;
    }

    try {
      await updateUser({
        oid: user.oid,
        body: {
          practiceId: selectedGroup.id,
          ifMatchPracticeId: user.practiceId,
        },
        practiceId: user.practiceId,
      }).unwrap();
      handleClose();
    } catch (err: any) {
      if (err?.status === 409) {
        setError("This user was updated by another admin. Please close and refresh to see the latest data.");
      } else {
        setError(err?.data?.message || err?.data || "Failed to update user.");
      }
    }
  };

  const handleClose = () => {
    setSelectedGroup(null);
    setError(null);
    onClose();
  };

  if (!user) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Portal User</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Name"
            value={user.displayName}
            disabled
            fullWidth
          />
          <TextField
            label="Email"
            value={user.mail}
            disabled
            fullWidth
          />
          <Autocomplete
            options={groups}
            getOptionLabel={(option) => option.name}
            value={selectedGroup}
            onChange={(_event, newValue) => setSelectedGroup(newValue)}
            loading={isLoadingGroups}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Practice"
                required
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingGroups ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading || !selectedGroup}
          startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
        >
          {isLoading ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
